import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import MerchantLogin from '../_models/MerchantLogin';
import React, { useEffect } from 'react';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { PaymentGateway } from '../_models/PaymentGateway';

const GATEWAY_UI_CONFIG: Record<string, { fields: string[]; required: string[] }> = {
  [PaymentGateway.none]: { fields: [], required: [] },
  [PaymentGateway.stripe]: {
    fields: ["api_key"],
    required: ["api_key"]
  },
  [PaymentGateway.cenpos]: {
    fields: ["merchantid", "merchantname", "primaryurl", "username", "password"],
    required: ["merchantid", "merchantname", "primaryurl", "username", "password"]
  },
  [PaymentGateway.tempus]: {
    fields: ["merchantid", "merchantname", "primaryurl", "username", "password"],
    required: ["merchantid", "merchantname", "primaryurl", "username", "password"],
  },
  [PaymentGateway.windcave]: {
    fields: ["username", "password"],
    required: ["username", "password"],
  },
  [PaymentGateway.xolvis]: {
    fields: ["username", "password", "api_key", "api_key_2"],
    required: ["username", "password", "api_key", "api_key_2"]
  }
};

const FIELD_LABELS: Record<string, string> = {
  merchantid: "Merchant ID",
  merchantname: "Merchant Name",
  primaryurl: "Primary URL",
  username: "Username",
  password: "Password",
  api_key: "API Key",
  api_key_2: "API Key 2"
};

interface MerchantLoginFormProps {
  readonly merchantLoginToEdit?: MerchantLogin;
  readonly handleSubmit: (tollAccount: MerchantLogin) => void;
}

export const MerchantLoginForm: React.FC<MerchantLoginFormProps> = (props) => {
  const [state, setState] = React.useState<MerchantLogin>({
    paymentgateway: PaymentGateway.none
  } as MerchantLogin);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState(false);
  const originalRef = React.useRef<MerchantLogin | null>(null);
  const config =
    GATEWAY_UI_CONFIG[state.paymentgateway ?? PaymentGateway.none] ||
    { fields: [], required: [] };
  const hasCipherblock = !!(state as any)?.cipherblock;

  useEffect(() => {
    if (props.merchantLoginToEdit != null) {
      const data = props.merchantLoginToEdit;
      setState(data);
      originalRef.current = JSON.parse(JSON.stringify(data));
    } else {
      setState({ paymentgateway: PaymentGateway.none } as MerchantLogin);
      originalRef.current = null;
    }
    setErrors({});
  }, [props.merchantLoginToEdit]);

  function validateForm() {
    const newErrors: Record<string, string> = {};
    if (!state.paymentgateway || state.paymentgateway === PaymentGateway.none) {
      newErrors.paymentgateway = "Please select a payment gateway";
    }
    config.required.forEach((field) => {
      const value = (state as any)[field];
      if (!value || value.trim() === "") {
        newErrors[field] = "This field is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }
  const hiddenFields = ["password", "username", "api_key", "api_key_2"];
  return (
    <div style={{ position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10
          }}
        >
          <CircularProgress />
        </div>
      )}
      <div style={{
        opacity: loading ? 0.4 : 1,
        pointerEvents: loading ? "none" : "auto"
      }}>
        <Grid container>
          <Grid item xs={4}>Payment Gateway</Grid>
          <Grid item xs={8}>
            <Select
              value={state.paymentgateway || PaymentGateway.none}
              style={{ width: '100%' }}
              error={!!errors.paymentgateway}
              onChange={(event: any) => {
                const newGateway = event.target.value;
                setState(() => {
                  const newConfig = GATEWAY_UI_CONFIG[newGateway] || { fields: [] };
                  const original = originalRef.current;
                  setErrors({});
                  if (original && original.paymentgateway === newGateway) {
                    const newState: any = {
                      paymentgateway: newGateway,
                      cipherblock: original.cipherblock
                    };
                    newConfig.fields.forEach((field) => {
                      newState[field] = (original as any)[field] ?? "";
                    });
                    return newState;
                  }
                  const fresh: any = {
                    paymentgateway: newGateway,
                    cipherblock: undefined
                  };
                  newConfig.fields.forEach((field) => {
                    fresh[field] = "";
                  });
                  return fresh;
                });
              }}
            >
              <MenuItem value={PaymentGateway.none}>No payment gateway</MenuItem>
              <MenuItem value={PaymentGateway.cenpos}>Cenpos</MenuItem>
              <MenuItem value={PaymentGateway.tempus}>Tempus</MenuItem>
              <MenuItem value={PaymentGateway.stripe}>Stripe</MenuItem>
              <MenuItem value={PaymentGateway.windcave}>Windcave</MenuItem>
              <MenuItem value={PaymentGateway.xolvis}>Xolvis</MenuItem>
            </Select>
            {errors.paymentgateway && (
              <div style={{ color: "red", fontSize: "12px", marginTop: 4 }}>
                {errors.paymentgateway}
              </div>
            )}
          </Grid>
        </Grid>
        {config.fields.map((field) => (
          <Grid container spacing={2} key={field}>
            <Grid item xs={4}>
              {FIELD_LABELS[field] || field}
            </Grid>
            <Grid item xs={8}>
              <TextField
                type={hiddenFields.some(f => field.includes(f)) ? "password" : "text"}
                value={(state as any)[field] || ""}
                placeholder={hasCipherblock ? "••••••••••••••" : ""}
                error={!!errors[field]}
                helperText={errors[field]}
                onFocus={() => {
                  if (hasCipherblock) {
                    setState((prev) => ({
                      ...prev,
                      cipherblock: undefined,
                      [field]: ""
                    }));
                  }
                }}
                onChange={(event) => {
                  const val = event.target.value;
                  setState((prev) => ({
                    ...prev,
                    [field]: val
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    [field]: ""
                  }));
                }}
                required={config.required.includes(field)}
                style={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        ))}
        <Grid container>
          <Grid item xs={4}></Grid>
          <Grid item xs={8} style={{ marginTop: "18px" }}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading}
              onClick={async () => {
                const ok = validateForm();
                if (!ok) return;

                try {
                  setLoading(true);
                  await props.handleSubmit(state);
                } finally {
                  setLoading(false);
                }
              }}
            >
              {loading ? <CircularProgress size={18} /> : "Save"}
            </Button>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};